"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useEmoji } from "@/contexts/EmojiContext";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import type { ApiAddReactionToDropRequest } from "@/generated/models/ApiAddReactionToDropRequest";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropContextProfileContext } from "@/generated/models/ApiDropContextProfileContext";
import type { ApiDropReaction } from "@/generated/models/ApiDropReaction";
import { formatLargeNumber } from "@/helpers/Helpers";
import { buildTooltipId } from "@/helpers/tooltip.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { commonApiDelete, commonApiPost } from "@/services/api/common-api";
import clsx from "clsx";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { cloneReactionEntries, findReactionIndex, removeUserFromReactions, toProfileMin } from "./reaction-utils";
import styles from "./WaveDropReactions.module.scss";
interface WaveDropReactionsProps {
  readonly drop: ApiDrop;
}
const WaveDropReactions: React.FC<WaveDropReactionsProps> = t0 => {
  const $ = _c(4);
  const {
    drop
  } = t0;
  let t1;
  if ($[0] !== drop) {
    t1 = drop.reactions.map(reaction => <WaveDropReaction key={`${reaction.reaction}-${reaction.profiles.length}`} drop={drop} reaction={reaction} />);
    $[0] = drop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <>{t1}</>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
function WaveDropReaction(t0) {
  const $ = _c(98);
  const {
    drop,
    reaction
  } = t0;
  const {
    setToast,
    connectedProfile
  } = useAuth();
  const {
    emojiMap,
    findNativeEmoji
  } = useEmoji();
  const {
    applyOptimisticDropUpdate
  } = useMyStream();
  const rollbackRef = useRef(null);
  const [total, setTotal] = useState(reaction.profiles.length);
  const [selected, setSelected] = useState(reaction.reaction === drop.context_profile_context?.reaction);
  let t1;
  if ($[0] !== reaction.profiles) {
    t1 = reaction.profiles.map(_temp);
    $[0] = reaction.profiles;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [handles, setHandles] = useState(t1);
  const [animate, setAnimate] = useState(false);
  const prevTotalRef = useRef(total);
  const prevContextReactionRef = useRef(drop.context_profile_context?.reaction);
  const prevProfilesRef = useRef(reaction.profiles);
  let t2;
  if ($[2] !== drop.context_profile_context?.reaction || $[3] !== reaction.reaction) {
    t2 = () => {
      if (drop.context_profile_context?.reaction === prevContextReactionRef.current) {
        return;
      }
      prevContextReactionRef.current = drop.context_profile_context?.reaction;
      const isSelected = reaction.reaction === drop.context_profile_context?.reaction;
      const timeoutId = setTimeout(() => {
        setSelected(current => current === isSelected ? current : isSelected);
      }, 0);
      return () => clearTimeout(timeoutId);
    };
    $[2] = drop.context_profile_context?.reaction;
    $[3] = reaction.reaction;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = drop.context_profile_context?.reaction;
  let t4;
  if ($[5] !== reaction.reaction || $[6] !== t3) {
    t4 = [t3, reaction.reaction];
    $[5] = reaction.reaction;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t2, t4);
  let t5;
  let t6;
  if ($[8] !== reaction.profiles) {
    t5 = () => {
      if (reaction.profiles === prevProfilesRef.current) {
        return;
      }
      prevProfilesRef.current = reaction.profiles;
      const nextTotal = reaction.profiles.length;
      const nextHandles = reaction.profiles.map(_temp2);
      const timeoutId_0 = setTimeout(() => {
        setTotal(current_0 => current_0 === nextTotal ? current_0 : nextTotal);
        setHandles(current_1 => {
          const sameLength = current_1.length === nextHandles.length;
          const sameValues = sameLength ? current_1.every((value, index) => value === nextHandles[index]) : false;
          return sameValues ? current_1 : nextHandles;
        });
      }, 0);
      return () => clearTimeout(timeoutId_0);
    };
    t6 = [reaction.profiles];
    $[8] = reaction.profiles;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[11] !== total) {
    t7 = () => {
      if (total === prevTotalRef.current) {
        return;
      }
      prevTotalRef.current = total;
      const timeoutId_1 = setTimeout(() => setAnimate(true), 0);
      return () => clearTimeout(timeoutId_1);
    };
    t8 = [total];
    $[11] = total;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== animate) {
    t9 = () => {
      if (!animate) {
        return;
      }
      const timeout = setTimeout(() => setAnimate(false), 100);
      return () => clearTimeout(timeout);
    };
    t10 = [animate];
    $[14] = animate;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  if ($[17] !== reaction.reaction) {
    t11 = reaction.reaction.replaceAll(":", "");
    $[17] = reaction.reaction;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const emojiId = t11;
  let t12;
  if ($[19] !== drop.id || $[20] !== emojiId) {
    t12 = buildTooltipId("reaction", drop.id, emojiId);
    $[19] = drop.id;
    $[20] = emojiId;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const tooltipId = t12;
  const waveId = drop.wave.id;
  let t13;
  bb0: {
    let t14;
    if ($[22] !== emojiId || $[23] !== emojiMap) {
      let t15;
      if ($[25] !== emojiId) {
        t15 = e => e.id === emojiId;
        $[25] = emojiId;
        $[26] = t15;
      } else {
        t15 = $[26];
      }
      t14 = emojiMap.flatMap(_temp3).find(t15);
      $[22] = emojiId;
      $[23] = emojiMap;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    const custom = t14;
    const customSrc = custom?.skins[0]?.src;
    if (customSrc) {
      let t15;
      if ($[27] !== customSrc || $[28] !== emojiId) {
        t15 = <div className="tw-relative tw-size-4"><Image src={customSrc} alt={emojiId} fill={true} className="tw-object-contain" /></div>;
        $[27] = customSrc;
        $[28] = emojiId;
        $[29] = t15;
      } else {
        t15 = $[29];
      }
      let t16;
      if ($[30] !== customSrc || $[31] !== emojiId) {
        t16 = <div className="tw-relative tw-size-8"><Image src={customSrc} alt={emojiId} fill={true} className="tw-rounded-sm tw-object-contain" /></div>;
        $[30] = customSrc;
        $[31] = emojiId;
        $[32] = t16;
      } else {
        t16 = $[32];
      }
      let t17;
      if ($[33] !== t15 || $[34] !== t16) {
        t17 = {
          emojiNode: t15,
          emojiNodeTooltip: t16
        };
        $[33] = t15;
        $[34] = t16;
        $[35] = t17;
      } else {
        t17 = $[35];
      }
      t13 = t17;
      break bb0;
    }
    let t15;
    if ($[36] !== emojiId || $[37] !== findNativeEmoji) {
      t15 = findNativeEmoji(emojiId);
      $[36] = emojiId;
      $[37] = findNativeEmoji;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    const native = t15;
    if (native) {
      const t16 = native.skins[0]?.native;
      let t17;
      if ($[39] !== t16) {
        t17 = <span className="tw-flex tw-items-center tw-justify-center tw-text-[1rem]">{t16}</span>;
        $[39] = t16;
        $[40] = t17;
      } else {
        t17 = $[40];
      }
      const t18 = native.skins[0]?.native;
      let t19;
      if ($[41] !== t18) {
        t19 = <span className="tw-flex tw-items-center tw-justify-center tw-text-2xl">{t18}</span>;
        $[41] = t18;
        $[42] = t19;
      } else {
        t19 = $[42];
      }
      let t20;
      if ($[43] !== t17 || $[44] !== t19) {
        t20 = {
          emojiNode: t17,
          emojiNodeTooltip: t19
        };
        $[43] = t17;
        $[44] = t19;
        $[45] = t20;
      } else {
        t20 = $[45];
      }
      t13 = t20;
      break bb0;
    }
    let t16;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        emojiNode: null,
        emojiNodeTooltip: null
      };
      $[46] = t16;
    } else {
      t16 = $[46];
    }
    t13 = t16;
  }
  const {
    emojiNode,
    emojiNodeTooltip
  } = t13;
  let t14;
  if ($[47] !== applyOptimisticDropUpdate || $[48] !== connectedProfile || $[49] !== drop.context_profile_context || $[50] !== drop.id || $[51] !== reaction.reaction || $[52] !== waveId) {
    t14 = willSelect => {
      if (!waveId) {
        return;
      }
      const userProfileMin = toProfileMin(connectedProfile);
      rollbackRef.current = applyOptimisticDropUpdate({
        waveId,
        dropId: drop.id,
        update: draft => {
          if (draft.type !== DropSize.FULL) {
            return draft;
          }
          const reactions = cloneReactionEntries(draft.reactions);
          const userId = connectedProfile?.id ?? null;
          const reactionsWithoutUser = removeUserFromReactions(reactions, userId);
          if (willSelect && userProfileMin) {
            const existingIndex = findReactionIndex(reactionsWithoutUser, reaction.reaction);
            if (existingIndex >= 0) {
              const target = reactionsWithoutUser[existingIndex];
              reactionsWithoutUser[existingIndex] = {
                ...target,
                profiles: [...target.profiles, userProfileMin]
              };
            } else {
              reactionsWithoutUser.push({
                reaction: reaction.reaction,
                profiles: [userProfileMin]
              });
            }
          }
          draft.reactions = reactionsWithoutUser;
          const existingContext = draft.context_profile_context ?? drop.context_profile_context ?? {
            rating: 0,
            min_rating: 0,
            max_rating: 0,
            reaction: null,
            boosted: false
          };
          draft.context_profile_context = {
            ...existingContext,
            reaction: willSelect ? reaction.reaction : null
          };
          return draft;
        }
      })?.rollback ?? null;
    };
    $[47] = applyOptimisticDropUpdate;
    $[48] = connectedProfile;
    $[49] = drop.context_profile_context;
    $[50] = drop.id;
    $[51] = reaction.reaction;
    $[52] = waveId;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  const applyOptimisticReactionChange = t14;
  let t15;
  if ($[54] !== applyOptimisticReactionChange || $[55] !== connectedProfile?.handle || $[56] !== connectedProfile?.id || $[57] !== drop.id || $[58] !== reaction.reaction || $[59] !== selected || $[60] !== setToast) {
    t15 = async () => {
      const resolvedHandle = connectedProfile?.handle ?? connectedProfile?.id ?? "";
      setSelected(_temp4);
      setTotal(n => Math.max(0, n + (selected ? -1 : 1)));
      if (selected) {
        setHandles(h => resolvedHandle ? h.filter(value_0 => value_0 !== resolvedHandle) : h);
      } else {
        setHandles(h_0 => {
          if (!resolvedHandle) {
            return h_0;
          }
          const nextHandles_0 = [...h_0, resolvedHandle];
          return Array.from(new Set(nextHandles_0));
        });
      }
      applyOptimisticReactionChange(!selected);
      ;
      try {
        const body = {
          reaction: reaction.reaction
        };
        const endpoint = `drops/${drop.id}/reaction`;
        if (selected) {
          await commonApiDelete({
            endpoint
          });
        } else {
          await commonApiPost({
            endpoint,
            body
          });
        }
      } catch (t16) {
        const error = t16;
        let msg = selected ? "Error removing reaction" : "Error adding reaction";
        if (typeof error === "string") {
          msg = error;
        }
        setToast({
          message: msg,
          type: "error"
        });
        setSelected(_temp5);
        setTotal(n_0 => Math.max(0, n_0 + (selected ? 1 : -1)));
        if (selected) {
          setHandles(h_1 => resolvedHandle ? Array.from(new Set([...h_1, resolvedHandle])) : h_1);
        } else {
          setHandles(h_2 => resolvedHandle ? h_2.filter(value_1 => value_1 !== resolvedHandle) : h_2);
        }
        rollbackRef.current?.();
        rollbackRef.current = null;
      }
      rollbackRef.current = null;
    };
    $[54] = applyOptimisticReactionChange;
    $[55] = connectedProfile?.handle;
    $[56] = connectedProfile?.id;
    $[57] = drop.id;
    $[58] = reaction.reaction;
    $[59] = selected;
    $[60] = setToast;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  connectedProfile?.handle;
  connectedProfile?.id;
  const handleClick = t15;
  let t16;
  if ($[62] !== handles || $[63] !== total) {
    bb1: {
      const truncate = _temp6;
      const truncatedHandles = handles.map(truncate);
      if (total <= 3) {
        t16 = truncatedHandles.join(", ");
        break bb1;
      }
      t16 = `${truncatedHandles.slice(0, 3).join(", ")} and ${total - 3} more`;
    }
    $[62] = handles;
    $[63] = total;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  const tooltipText = t16;
  const borderStyle = selected ? "tw-border-primary-500" : "tw-border-iron-700";
  const bgStyle = selected ? "tw-bg-primary-500/10" : "tw-bg-iron-900/40";
  const hoverStyle = selected ? "hover:tw-border-primary-500 hover:tw-bg-primary-500/10" : "hover:tw-border-iron-500 hover:tw-bg-iron-900/40";
  let animationStyle = "";
  if (animate) {
    if (selected) {
      animationStyle = styles.reactionSlideUp;
    } else {
      animationStyle = styles.reactionSlideDown;
    }
  }
  if (!emojiNode || total === 0) {
    return null;
  }
  let t17;
  if ($[65] !== bgStyle || $[66] !== borderStyle || $[67] !== hoverStyle) {
    t17 = clsx("tw-mt-1 tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-px-2 tw-py-1 tw-shadow-sm hover:tw-text-iron-100", borderStyle, bgStyle, hoverStyle);
    $[65] = bgStyle;
    $[66] = borderStyle;
    $[67] = hoverStyle;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== emojiNode) {
    t18 = <div className="tw-flex tw-size-5 tw-flex-shrink-0 tw-items-center tw-justify-center">{emojiNode}</div>;
    $[69] = emojiNode;
    $[70] = t18;
  } else {
    t18 = $[70];
  }
  let t19;
  if ($[71] !== animationStyle) {
    t19 = clsx("tw-min-w-[2ch] tw-text-xs tw-font-normal", animationStyle);
    $[71] = animationStyle;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  let t20;
  if ($[73] !== total) {
    t20 = formatLargeNumber(total);
    $[73] = total;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  let t21;
  if ($[75] !== t19 || $[76] !== t20) {
    t21 = <span className={t19}>{t20}</span>;
    $[75] = t19;
    $[76] = t20;
    $[77] = t21;
  } else {
    t21 = $[77];
  }
  let t22;
  if ($[78] !== t18 || $[79] !== t21) {
    t22 = <div className="tw-flex tw-h-full tw-items-center tw-gap-x-1">{t18}{t21}</div>;
    $[78] = t18;
    $[79] = t21;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== handleClick || $[82] !== t17 || $[83] !== t22 || $[84] !== tooltipId) {
    t23 = <button onClick={handleClick} data-tooltip-id={tooltipId} data-text-selection-exclude="true" className={t17}>{t22}</button>;
    $[81] = handleClick;
    $[82] = t17;
    $[83] = t22;
    $[84] = tooltipId;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      backgroundColor: "#37373E",
      color: "white",
      zIndex: 50
    };
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== tooltipText) {
    t25 = <span className="tw-whitespace-nowrap">by {tooltipText}</span>;
    $[87] = tooltipText;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== emojiNodeTooltip || $[90] !== t25) {
    t26 = <div className="tw-flex tw-items-center tw-gap-2">{emojiNodeTooltip}{t25}</div>;
    $[89] = emojiNodeTooltip;
    $[90] = t25;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] !== t26 || $[93] !== tooltipId) {
    t27 = <Tooltip id={tooltipId} delayShow={250} place="bottom" opacity={1} style={t24}>{t26}</Tooltip>;
    $[92] = t26;
    $[93] = tooltipId;
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== t23 || $[96] !== t27) {
    t28 = <>{t23}{t27}</>;
    $[95] = t23;
    $[96] = t27;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  return t28;
}
function _temp6(handle) {
  return handle.length > 12 ? handle.slice(0, 12) + "\u2026" : handle;
}
function _temp5(s_0) {
  return !s_0;
}
function _temp4(s) {
  return !s;
}
function _temp3(cat) {
  return cat.emojis;
}
function _temp2(p_0) {
  return p_0.handle ?? p_0.id;
}
function _temp(p) {
  return p.handle ?? p.id;
}
export default WaveDropReactions;